import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Boxes, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
export default function Inventory() {
    const [items, setItems] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAdj, setOpenAdj] = useState(false);
    const [selected, setSelected] = useState(null);
    const [adjChange, setAdjChange] = useState(0);
    const [adjReason, setAdjReason] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [pr, lr, lgr] = await Promise.all([
                api.get('/products', { params: { limit: 500 } }),
                api.get('/inventory/low-stock'),
                api.get('/inventory/logs', { params: { limit: 50 } }),
            ]);
            setItems(pr.data.items); setLowStock(lr.data); setLogs(lgr.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openAdjust = (p) => { setSelected(p); setAdjChange(0); setAdjReason(''); setOpenAdj(true); };
    const submitAdj = async () => {
        try {
            await api.post('/inventory/adjust', { product_id: selected.id, change: parseInt(adjChange), reason: adjReason || 'Manual adjustment' });
            toast.success('Inventory adjusted'); setOpenAdj(false); load();
        } catch (e) { toast.error(errMsg(e, 'Adjustment failed')); }
    };

    return (
        <AppShell pageTitle="Inventory">
            {loading ? <LoadingBlock /> : (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <div className="xl:col-span-2 space-y-4">
                        <Card className="p-0 overflow-x-auto">
                            <div className="p-4 border-b flex items-center justify-between">
                                <div className="text-sm font-semibold">Stock Levels ({items.length})</div>
                            </div>
                            {items.length === 0 ? (
                                <EmptyState icon={Boxes} title="No products" description="Create products first." />
                            ) : (
                                <table className="w-full text-sm" data-testid="inventory-table">
                                    <thead>
                                        <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                            <th className="text-left p-3 font-medium">Product</th>
                                            <th className="text-left font-medium">SKU</th>
                                            <th className="text-right font-medium">Available</th>
                                            <th className="text-right font-medium">Reserved</th>
                                            <th className="text-right font-medium">Threshold</th>
                                            <th className="text-right p-3 font-medium">Adjust</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(p => {
                                            const av = p.inventory?.available || 0;
                                            const low = av <= (p.low_stock_threshold || 5);
                                            return (
                                                <tr key={p.id} className={`border-b hover:bg-neutral-50 ${low ? 'bg-red-50/40' : ''}`} data-testid={`inventory-row-${p.sku}`}>
                                                    <td className="p-3">{p.name}</td>
                                                    <td className="font-mono text-xs">{p.sku}</td>
                                                    <td className={`text-right tabular-nums ${low ? 'text-red-600 font-medium' : ''}`}>{av}</td>
                                                    <td className="text-right tabular-nums">{p.inventory?.reserved || 0}</td>
                                                    <td className="text-right tabular-nums text-neutral-600">{p.low_stock_threshold || 5}</td>
                                                    <td className="p-3 text-right">
                                                        <Button variant="outline" size="sm" onClick={() => openAdjust(p)} data-testid={`adjust-${p.sku}-button`}>Adjust</Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </Card>
                    </div>
                    <div className="space-y-4">
                        <Card className="p-4">
                            <div className="text-sm font-semibold mb-3">Low Stock ({lowStock.length})</div>
                            {lowStock.length === 0 ? <div className="text-xs text-neutral-500">All good — no low stock alerts.</div> : (
                                <div className="space-y-2">
                                    {lowStock.map(l => (
                                        <div key={l.id} className="flex items-center gap-2 text-sm p-2 rounded-md bg-red-50" data-testid={`low-stock-${l.sku}`}>
                                            <div className="flex-1"><div className="font-medium">{l.name}</div><div className="text-xs text-neutral-600 font-mono">{l.sku}</div></div>
                                            <div className="tabular-nums text-red-700 font-semibold">{l.inventory?.available}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                        <Card className="p-4">
                            <div className="text-sm font-semibold mb-3">Recent Activity</div>
                            {logs.length === 0 ? <div className="text-xs text-neutral-500">No activity.</div> : (
                                <div className="space-y-2">
                                    {logs.slice(0, 12).map(l => (
                                        <div key={l.id} className="text-xs flex items-start gap-2">
                                            {(l.change || 0) >= 0 ? <TrendingUp className="h-3 w-3 text-green-600 mt-0.5" /> : <TrendingDown className="h-3 w-3 text-red-600 mt-0.5" />}
                                            <div className="flex-1">
                                                <div><span className="font-mono">{l.sku}</span> · {l.reason}</div>
                                                <div className="text-neutral-500">{new Date(l.at).toLocaleString()} by {l.by || 'system'}</div>
                                            </div>
                                            <div className={`tabular-nums font-medium ${l.change >= 0 ? 'text-green-700' : 'text-red-700'}`}>{l.change > 0 ? '+' : ''}{l.change}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            )}

            <Dialog open={openAdj} onOpenChange={setOpenAdj}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Adjust Inventory — {selected?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm text-neutral-600">Current stock: <span className="font-semibold text-neutral-900">{selected?.inventory?.available || 0}</span></div>
                        <div>
                            <Label>Change (positive to add, negative to deduct)</Label>
                            <Input type="number" value={adjChange} onChange={e => setAdjChange(e.target.value)} data-testid="adjustment-change-input" />
                        </div>
                        <div><Label>Reason</Label><Input value={adjReason} onChange={e => setAdjReason(e.target.value)} placeholder="Received shipment, Damaged, Recount, etc." data-testid="adjustment-reason-input" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenAdj(false)}>Cancel</Button>
                        <Button onClick={submitAdj} data-testid="submit-adjustment-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Adjust</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
